import config, { resolveApiUrl } from '../config/environment';
import { enqueueOfflineMutation } from './syncManager';
import offlineDb from './offlineDb';
import logger from '../utils/logger';
interface ApiResponse<T = any> {
  success: boolean;
  code?: string;
  data?: T;
  message?: string;
  error?: string;
  token?: string;
  user?: any;
  total?: number;
  requiresTOTP?: boolean;
}

// In-memory token — set by AuthContext on login; never written to localStorage.
// The httpOnly auth_token cookie handles session persistence across page reloads.
let _inMemoryToken: string | null = null;

export function setInMemoryToken(token: string | null) {
  _inMemoryToken = token;
}

export function getInMemoryToken(): string | null {
  return _inMemoryToken;
}

export function getAuthHeaders(headers: HeadersInit = {}): Record<string, string> {
  const authHeaders: Record<string, string> = {};
  const requestHeaders: Record<string, string> = {};

  new Headers(headers).forEach((value, key) => {
    requestHeaders[key] = value;
  });

  if (_inMemoryToken) {
    authHeaders['Authorization'] = `Bearer ${_inMemoryToken}`;
  }

  return {
    ...authHeaders,
    ...requestHeaders,
  };
}

export function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(resolveApiUrl(url), {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers),
  });
}

// Error codes that mean the session itself is dead. Anything else — including
// every 5xx — means "we don't know", and must never end a valid session.
// The backend answers 401 for all of these; 403 is still accepted because the
// frontend and backend deploy independently, so an older backend that still
// returns 403 for AUTH_TOKEN_INVALID must not strand the user in a zombie
// session. Plan/permission 403s carry different codes and are unaffected.
export const AUTH_CODES = [
  'AUTH_TOKEN_EXPIRED',
  'AUTH_TOKEN_INVALID',
  'AUTH_TOKEN_REQUIRED',
  'AUTH_USER_NOT_FOUND',
];

/** True only for a genuine auth failure — the one case that justifies a logout. */
export function isAuthFailure(status: number, data: any): boolean {
  return (status === 401 || status === 403) && AUTH_CODES.includes(data?.code);
}

/**
 * True when the backend is unreachable rather than rejecting us: a proxy 5xx
 * during a deploy, or the service worker's synthetic `{ offline: true }` 503.
 * Callers must keep the session and retry, never log the user out.
 */
export function isBackendUnavailable(status: number, data?: any): boolean {
  return status === 502 || status === 503 || status === 504 || !!data?.offline;
}

function isTransientNetworkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch') ||
    message.includes('Network changed') ||
    message.includes('NAME_NOT_RESOLVED') ||
    message.includes('ECONNRESET') ||
    message.includes('Load failed') // Safari's wording for a dropped connection
  );
}

// Exponential backoff with jitter, capped, and bounded by a total deadline.
// The window is a balance: long enough to absorb a quick restart so the request
// just succeeds, short enough that a real outage falls back to the IndexedDB
// cache promptly instead of hanging the UI. Outages longer than this are the
// reconnect watcher's job — it refetches everything once the backend answers.
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 5_000;
const RETRY_TOTAL_WINDOW_MS = 15_000;

// Session restore is on the critical path to first paint, so it gets a much
// shorter window: long enough to ride out a blip, short enough that a real
// outage doesn't hold the user on a spinner. Past this we render from cache in
// a degraded state and let the reconnect watcher finish the job.
export const BOOTSTRAP_RETRY_WINDOW_MS = 5_000;

function backoffDelay(attempt: number): number {
  const exponential = Math.min(RETRY_BASE_DELAY_MS * 2 ** attempt, RETRY_MAX_DELAY_MS);
  return exponential / 2 + Math.random() * (exponential / 2); // full-ish jitter
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * authenticatedFetch that rides out a backend restart.
 *
 * Retries transient network errors AND proxy 5xx / offline-sentinel responses
 * until `windowMs` elapses. Any decisive answer — 2xx, 4xx, or a 5xx that isn't
 * an availability signal — returns immediately. Only for idempotent requests;
 * mutations go through the offline queue instead.
 */
export async function authenticatedFetchWithRetry(
  url: string,
  options: RequestInit = {},
  windowMs: number = RETRY_TOTAL_WINDOW_MS
): Promise<Response> {
  const deadline = Date.now() + windowMs;
  let attempt = 0;

  for (;;) {
    try {
      const response = await authenticatedFetch(url, options);
      if (!isBackendUnavailable(response.status)) return response;

      if (Date.now() >= deadline) return response; // out of time — let caller see the 5xx
      logger.warn(`API ${url} returned ${response.status} — backend likely restarting, retrying...`);
    } catch (error) {
      if (!isTransientNetworkError(error) || Date.now() >= deadline) throw error;
      logger.warn(`API ${url} network error — retrying...`, error);
    }

    await sleep(backoffDelay(attempt));
    attempt += 1;
  }
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = resolveApiUrl(endpoint);
    const method = (options.method || 'GET').toUpperCase();
    const isMutation = method !== 'GET';

    const fetchConfig: RequestInit = {
      ...options,
      credentials: 'include',
      headers: getAuthHeaders(options.headers),
    };

    if (
      fetchConfig.body &&
      typeof fetchConfig.body === 'string' &&
      (!fetchConfig.headers ||
        !((fetchConfig.headers as Record<string, string>)['Content-Type'] ||
          (fetchConfig.headers as Record<string, string>)['content-type']))
    ) {
      (fetchConfig.headers as Record<string, string>)['Content-Type'] = 'application/json; charset=utf-8';
    }

    // A deploy outlasts any short retry loop, so GETs ride out the whole
    // window. Mutations never retry here — they go to the offline queue, which
    // replays them once the backend is back.
    const deadline = Date.now() + RETRY_TOTAL_WINDOW_MS;

    for (let attempt = 0; ; attempt++) {
      try {
        const response = await fetch(url, fetchConfig);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // Only logout on genuine auth failures, not on any 401/403.
          // This prevents non-auth routes that mis-return 401 from kicking the user out.
          if (isAuthFailure(response.status, data)) {
            logger.log('🔒 Auth invalid (', data?.code, ') - triggering logout');
            window.dispatchEvent(new CustomEvent('tokenExpired', {
              detail: { message: 'Your session is invalid or expired. Please log in again.' }
            }));
            return {
              success: false,
              code: data?.code,
              message: data.message || `HTTP error! status: ${response.status}`,
              error: data.error || data.message || `Request failed (HTTP ${response.status})`
            };
          }

          // Backend unavailable (proxy 5xx during a deploy, or the SW's offline
          // sentinel). Keep retrying until the window closes, then fall back.
          if (isBackendUnavailable(response.status, data)) {
            if (!isMutation && Date.now() < deadline) {
              logger.warn(`API ${endpoint} returned ${response.status} — backend likely restarting, retrying...`);
              await sleep(backoffDelay(attempt));
              continue;
            }

            if (!isMutation) {
              const cached = await this.getCachedResponse<T>(endpoint);
              if (cached) return cached;
            }

            if (isMutation && typeof fetchConfig.body === 'string') {
              return this.queueOfflineMutation<T>(method, endpoint, fetchConfig.body);
            }
          }

          return {
            success: false,
            code: data?.code,
            message: data.message || `HTTP error! status: ${response.status}`,
            error: data.error || data.message || `Request failed (HTTP ${response.status})`
          };
        }

        // Cache GET responses in IndexedDB for offline fallback
        if (!isMutation) {
          this.cacheResponse(endpoint, data);
        }

        return data;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        if (!isMutation && isTransientNetworkError(error) && Date.now() < deadline) {
          logger.warn(`API request failed (attempt ${attempt + 1}), retrying...`, errorMessage);
          await sleep(backoffDelay(attempt));
          continue;
        }

        // Out of time (or a mutation) — try offline fallback
        if (isMutation && typeof fetchConfig.body === 'string') {
          return this.queueOfflineMutation<T>(method, endpoint, fetchConfig.body);
        }

        if (!isMutation) {
          const cached = await this.getCachedResponse<T>(endpoint);
          if (cached) return cached;
        }

        logger.error('API request failed after retries:', error);
        return {
          success: false,
          message: 'Network error',
          error: errorMessage
        };
      }
    }
  }

  private async queueOfflineMutation<T>(method: string, endpoint: string, body: string): Promise<ApiResponse<T>> {
    try {
      let parsedBody: any;
      try { parsedBody = JSON.parse(body); } catch { parsedBody = undefined; }
      await enqueueOfflineMutation(method, endpoint, parsedBody);
      logger.log('📴 Mutation queued for sync:', method, endpoint);
      return { success: true, message: 'Saved offline — will sync when back online' } as ApiResponse<T>;
    } catch (e) {
      logger.error('Failed to queue offline mutation:', e);
      return { success: false, message: 'Failed to save offline', error: 'IndexedDB error' };
    }
  }

  private cacheResponse(endpoint: string, data: any) {
    try {
      const storeMap: Record<string, string> = {
        '/api/appointments': 'appointments',
        '/api/patients': 'patients',
        '/api/procedures': 'procedures',
      };
      const storeName = storeMap[endpoint];
      if (!storeName) return;

      const records = data.appointments || data.data?.appointments || data.data || [];
      if (Array.isArray(records) && records.length > 0) {
        offlineDb.putAll(storeName, records).catch(() => {});
      }
    } catch { /* best effort */ }
  }

  private async getCachedResponse<T>(endpoint: string): Promise<ApiResponse<T> | null> {
    try {
      const storeMap: Record<string, string> = {
        '/api/appointments': 'appointments',
        '/api/patients': 'patients',
        '/api/procedures': 'procedures',
      };
      const storeName = storeMap[endpoint];
      if (!storeName) return null;

      const records = await offlineDb.getAll(storeName);
      if (!records.length) return null;

      logger.log('📴 Returning cached data for', endpoint);
      if (storeName === 'appointments') {
        return { success: true, appointments: records } as any;
      }
      return { success: true, data: records } as any;
    } catch {
      return null;
    }
  }

  // Auth endpoints
  async login(username: string, password: string, totpCode?: string): Promise<ApiResponse> {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, ...(totpCode ? { totpCode } : {}) }),
    });
  }

  async register(userData: any): Promise<ApiResponse> {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateCalendarSharingPreferences(prefs: { shareCalendarWithClinic?: boolean; showSharedCalendars?: boolean }): Promise<ApiResponse> {
    return this.request('/api/auth/preferences', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    });
  }

  // Appointments endpoints
  async getAppointments(): Promise<ApiResponse> {
    return this.request('/api/appointments');
  }

  async createAppointment(appointmentData: any): Promise<ApiResponse> {
    return this.request('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointment(id: number, appointmentData: any): Promise<ApiResponse> {
    return this.request(`/api/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData),
    });
  }

  async deleteAppointment(id: number): Promise<ApiResponse> {
    return this.request(`/api/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  // Patients endpoints
  async getPatients(): Promise<ApiResponse> {
    return this.request('/api/patients');
  }

  async createPatient(clientData: any): Promise<ApiResponse> {
    return this.request('/api/patients', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
  }

  async updatePatient(id: number, clientData: any): Promise<ApiResponse> {
    return this.request(`/api/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(clientData),
    });
  }

  async deletePatient(id: number): Promise<ApiResponse> {
    return this.request(`/api/patients/${id}`, {
      method: 'DELETE',
    });
  }

  // Aliases kept for backward compatibility during migration
  async getClients(): Promise<ApiResponse> { return this.getPatients(); }
  async createClient(data: any): Promise<ApiResponse> { return this.createPatient(data); }
  async updateClient(id: number, data: any): Promise<ApiResponse> { return this.updatePatient(id, data); }
  async deleteClient(id: number): Promise<ApiResponse> { return this.deletePatient(id); }

  // Procedures endpoints
  async getProcedures(): Promise<ApiResponse> {
    return this.request('/api/procedures');
  }

  async createProcedure(procedureData: any): Promise<ApiResponse> {
    return this.request('/api/procedures', {
      method: 'POST',
      body: JSON.stringify(procedureData),
    });
  }

  async updateProcedure(id: number, procedureData: any): Promise<ApiResponse> {
    return this.request(`/api/procedures/${id}`, {
      method: 'PUT',
      body: JSON.stringify(procedureData),
    });
  }

  async deleteProcedure(id: number): Promise<ApiResponse> {
    return this.request(`/api/procedures/${id}`, {
      method: 'DELETE',
    });
  }

  // Inventory endpoints
  async getInventoryItems(): Promise<ApiResponse> {
    return this.request('/api/inventory/items');
  }

  async getInventoryItem(id: number): Promise<ApiResponse> {
    return this.request(`/api/inventory/items/${id}`);
  }

  async createInventoryItem(data: any): Promise<ApiResponse> {
    return this.request('/api/inventory/items', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateInventoryItem(id: number, data: any): Promise<ApiResponse> {
    return this.request(`/api/inventory/items/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteInventoryItem(id: number): Promise<ApiResponse> {
    return this.request(`/api/inventory/items/${id}`, { method: 'DELETE' });
  }

  async getItemLots(itemId: number): Promise<ApiResponse> {
    return this.request(`/api/inventory/items/${itemId}/lots`);
  }

  async addItemLot(itemId: number, data: any): Promise<ApiResponse> {
    return this.request(`/api/inventory/items/${itemId}/lots`, { method: 'POST', body: JSON.stringify(data) });
  }

  async adjustLot(lotId: number, data: any): Promise<ApiResponse> {
    return this.request(`/api/inventory/lots/${lotId}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async getLowStock(): Promise<ApiResponse> {
    return this.request('/api/inventory/low-stock');
  }

  async getExpiring(days = 30): Promise<ApiResponse> {
    return this.request(`/api/inventory/expiring?days=${days}`);
  }

  async getProcedureDefaultItems(procedureId: number): Promise<ApiResponse> {
    return this.request(`/api/inventory/procedures/${procedureId}/default-items`);
  }

  async setProcedureDefaultItems(procedureId: number, items: any[]): Promise<ApiResponse> {
    return this.request(`/api/inventory/procedures/${procedureId}/default-items`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async getInventoryCatalog(): Promise<ApiResponse> {
    return this.request('/api/inventory/catalog');
  }

  async getAppointmentItems(appointmentId: number): Promise<ApiResponse> {
    return this.request(`/api/inventory/appointments/${appointmentId}/items`);
  }

  async setAppointmentItems(appointmentId: number, items: any[]): Promise<ApiResponse> {
    return this.request(`/api/inventory/appointments/${appointmentId}/items`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  async deductAppointmentItems(appointmentId: number, opts: any = {}): Promise<ApiResponse> {
    return this.request(`/api/inventory/appointments/${appointmentId}/deduct`, {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  }

  // Medical records endpoints
  async getMedicalRecords(): Promise<ApiResponse> {
    return this.request('/api/medical-records');
  }

  async createMedicalRecord(recordData: any): Promise<ApiResponse> {
    return this.request('/api/medical-records', {
      method: 'POST',
      body: JSON.stringify(recordData),
    });
  }

  async updateMedicalRecord(id: number, recordData: any): Promise<ApiResponse> {
    return this.request(`/api/medical-records/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recordData),
    });
  }

  async deleteMedicalRecord(id: number): Promise<ApiResponse> {
    return this.request(`/api/medical-records/${id}`, {
      method: 'DELETE',
    });
  }

  // Appointment notes and photos endpoints
  async getAppointmentNotes(appointmentId: number): Promise<ApiResponse> {
    return this.request(`/api/appointment-notes/${appointmentId}/notes`);
  }

  async updateAppointmentNotes(appointmentId: number, notes: string): Promise<ApiResponse> {
    return this.request(`/api/appointment-notes/${appointmentId}/notes`, {
      method: 'PUT',
      body: JSON.stringify({ notes }),
    });
  }

  async getAppointmentPictures(appointmentId: number): Promise<ApiResponse> {
    return this.request(`/api/appointment-notes/${appointmentId}/pictures`);
  }

  async uploadAppointmentPicture(
    appointmentId: number,
    file: File,
    description?: string
  ): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('picture', file);
    if (description) {
      formData.append('description', description);
    }

    return this.request(`/api/appointment-notes/${appointmentId}/pictures`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async deleteAppointmentPicture(appointmentId: number, pictureId: number): Promise<ApiResponse> {
    return this.request(`/api/appointment-notes/${appointmentId}/pictures/${pictureId}`, {
      method: 'DELETE',
    });
  }

  async getClientPhotos(clientId: number): Promise<ApiResponse> {
    return this.request(`/api/appointment-notes/client/${clientId}/photos`);
  }

  // Recall rule image (sent as WhatsApp media with the rule's message as caption).
  async uploadRecallRuleMedia(ruleId: number, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('media', file);
    return this.request(`/api/recall/rules/${ruleId}/media`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async deleteRecallRuleMedia(ruleId: number): Promise<ApiResponse> {
    return this.request(`/api/recall/rules/${ruleId}/media`, { method: 'DELETE' });
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.request('/api/health');
  }

  // Generic HTTP methods
  async get(endpoint: string): Promise<ApiResponse> {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint: string, data?: any): Promise<ApiResponse> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any): Promise<ApiResponse> {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any): Promise<ApiResponse> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<ApiResponse> {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Get image URL helper
  getImageUrl(filename: string): string {
    return resolveApiUrl(`/api/appointment-notes/pictures/${filename}`);
  }

  getThumbnailUrl(filename: string): string {
    return resolveApiUrl(`/api/appointment-notes/pictures/${filename}/thumb`);
  }
}

const apiService = new ApiService();
export default apiService;
export { resolveApiUrl };
